import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, BadgeCheck, FileText, HandCoins } from "lucide-react";
import { api } from "@/lib/api";
import { categoryPath } from "@/lib/paths";
import SEO from "@/components/SEO";
import RotatingTestimonial from "@/components/RotatingTestimonial";

const POPULAR_SEARCHES = ["bath towels", "quilts", "minibar", "pillows", "bed linen", "bathrobes"];

const BENEFITS = [
  { icon: HandCoins, title: "Free to use, always", text: "Search, compare and contact suppliers at no cost. There is no buyer account fee and never will be." },
  { icon: FileText, title: "Request quotes from up to 4 suppliers at once", text: "Send one brief from any category page and let the top-ranked suppliers come back to you." },
  { icon: BadgeCheck, title: "Verified suppliers only — no spam", text: "Your details go only to the suppliers you choose. Verified suppliers have completed their compliance profile." },
];

const UpdatesForm = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [state, setState] = useState({ busy: false, done: false, already: false, error: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: false, already: false, error: "" });
    try {
      const { data } = await api.post("/buyer-updates", { name: form.name.trim(), email: form.email.trim() });
      setState({ busy: false, done: true, already: !!data.already, error: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setState({ busy: false, done: false, already: false, error: typeof d === "string" ? d : "Please check your name and email and try again." });
    }
  };
  if (state.done) {
    return (
      <div className="border border-[#264839]/30 bg-white p-6" data-testid="for-hotels-updates-success">
        <div className="font-serif text-xl text-[#11211A]">{state.already ? "You're already on the list." : "You're on the list."}</div>
        <p className="mt-2 text-sm text-[#4A5550]">{state.already ? "We've kept your details up to date." : `We've sent a confirmation to ${form.email.trim()}.`} We'll email you as each new category opens.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="border border-[#E8ECE9] bg-white p-6" data-testid="for-hotels-updates-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fh-name" className="block text-xs font-medium text-[#4A5550] mb-1.5">Name</label>
          <input id="fh-name" required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full px-3 py-2.5 text-sm border border-[#C4CFC9] bg-white focus:border-[#264839] focus:outline-none" data-testid="for-hotels-updates-name" />
        </div>
        <div>
          <label htmlFor="fh-email" className="block text-xs font-medium text-[#4A5550] mb-1.5">Work email</label>
          <input id="fh-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@yourhotel.com" className="w-full px-3 py-2.5 text-sm border border-[#C4CFC9] bg-white focus:border-[#264839] focus:outline-none" data-testid="for-hotels-updates-email" />
        </div>
      </div>
      {state.error && <div className="mt-3 text-sm text-[#B35C4A]" role="alert" data-testid="for-hotels-updates-error">{state.error}</div>}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <button type="submit" disabled={state.busy} className="btn-primary disabled:opacity-60" data-testid="for-hotels-updates-submit">{state.busy ? "Sending…" : "Keep me updated"}</button>
        <span className="text-xs text-[#7D8C85]">Launch news only — no supplier marketing, unsubscribe any time.</span>
      </div>
    </form>
  );
};

export default function ForHotelsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [tree, setTree] = useState([]);
  const countCopy = "Browse suppliers";
  useEffect(() => { api.get("/categories/tree").then(({ data }) => setTree(data)).catch(() => {}); }, []);

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/search?q=${encodeURIComponent(term)}` : "/search");
  };

  return (
    <div data-testid="for-hotels-page">
      <SEO title="For Hotels — Find the right suppliers for your property" description={`${countCopy} across linen, amenities, furniture and more — free for buyers, always.`} path="/for-hotels" />

      <section className="bg-[#11211A] text-white" data-testid="for-hotels-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="tracking-overline text-[#D27C6A] mb-4">For hotels &amp; accommodation buyers</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="for-hotels-headline">Find the right suppliers for your property</h1>
            <p className="mt-6 text-base md:text-lg text-white/75 max-w-2xl" data-testid="for-hotels-subheading">{countCopy} across linen, amenities, furniture and more — free for buyers, always.</p>
            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="for-hotels-search-form">
              <div className="relative flex-1">
                <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#7D8C85]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products or suppliers — e.g. bath towels, quilts, minibar" className="w-full pl-12 pr-4 py-4 text-base text-[#11211A] bg-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#D27C6A]" aria-label="Search suppliers" data-testid="for-hotels-search-input" />
              </div>
              <button type="submit" className="px-6 py-4 text-base font-medium bg-white text-[#11211A] hover:bg-[#F0EFEA] transition-colors whitespace-nowrap" data-testid="for-hotels-search-submit">Search suppliers</button>
            </form>
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="for-hotels-popular-searches">
              <span className="text-xs uppercase tracking-widest text-white/60 mr-1">Popular</span>
              {POPULAR_SEARCHES.map((term) => (
                <Link key={term} to={`/search?q=${encodeURIComponent(term)}`} className="text-xs px-3 py-1.5 border border-white/30 text-white/90 hover:border-white hover:bg-white/10 transition-colors" data-testid={`for-hotels-popular-${term.replace(/\s+/g, "-")}`}>
                  {term}
                </Link>
              ))}
            </div>
            <Link to="/search" className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/90 border-b border-white/40 hover:border-white pb-0.5 transition-colors group" data-testid="for-hotels-browse-all">
              Browse all suppliers <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-testid="for-hotels-categories">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="tracking-overline mb-2">Browse by category</div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#11211A] tracking-tight">What are you sourcing?</h2>
          </div>
          <Link to="/categories" className="text-sm font-medium text-[#264839] border-b border-[#264839]/40 hover:border-[#264839] pb-0.5" data-testid="for-hotels-all-categories">All categories →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tree.map((cat) => {
            const inner = (
              <>
                <div className="aspect-[16/9] bg-[#F0EFEA] overflow-hidden">
                  {cat.image_url && <img src={cat.image_url} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />}
                </div>
                <div className="p-5 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-xl text-[#11211A]">{cat.name}</div>
                    <div className="text-xs text-[#7D8C85] mt-1" data-testid={`for-hotels-tile-count-${cat.slug || cat.id}`}>
                      {cat.launch_status === "launching" ? "Launching now, suppliers joining" : cat.is_coming_soon ? "Coming soon — register your interest" : "Explore suppliers"}
                    </div>
                  </div>
                  {cat.is_coming_soon
                    ? <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#7D8C85] px-2 py-0.5 border border-[#C4CFC9] whitespace-nowrap" data-testid={`for-hotels-launching-soon-${cat.slug || cat.id}`}>Coming soon</span>
                    : <ArrowRight className="w-5 h-5 text-[#264839] shrink-0 transition-transform group-hover:translate-x-1" />}
                </div>
              </>
            );
            const cls = "group block border border-[#E8ECE9] bg-white overflow-hidden";
            return <Link key={cat.id} to={categoryPath(cat)} className={`${cls} hover:border-[#264839] transition-colors`} data-testid={`for-hotels-tile-${cat.slug || cat.id}`}>{inner}</Link>;
          })}
        </div>
      </section>

      <RotatingTestimonialSection page="for-hotels" />

      <section className="border-t border-[#E8ECE9]" data-testid="for-hotels-updates">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="tracking-overline mb-2">Buyer updates</div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#11211A] tracking-tight" data-testid="for-hotels-updates-heading">We're just getting started</h2>
            <p className="mt-4 text-base text-[#4A5550] max-w-xl" data-testid="for-hotels-updates-text">Linen, Textiles &amp; Soft Furnishings is live now and Furniture, Fixtures &amp; Interior Design is launching. Six more categories are coming soon — register your interest on each category page.</p>
          </div>
          <UpdatesForm />
        </div>
      </section>

      <section className="bg-[#F0EFEA]" data-testid="for-hotels-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {BENEFITS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="border-l-2 border-[#264839] pl-5" data-testid={`for-hotels-benefit-${i + 1}`}>
                <Icon className="w-6 h-6 text-[#264839] mb-4" />
                <h3 className="font-serif text-xl text-[#11211A]">{title}</h3>
                <p className="mt-2 text-sm text-[#4A5550]">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function RotatingTestimonialSection({ page }) {
  return (
    <section className="border-t border-[#E8ECE9] bg-white" data-testid="for-hotels-testimonial">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
        <RotatingTestimonial page={page} />
      </div>
    </section>
  );
}
